import { Box, Typography, Button, Paper, Divider } from "@mui/material";

import {
  Person,
  Phone,
  LocationOn,
  DirectionsCar,
  CurrencyRupee,
  AccountBalance,
  Description,
  Badge,
  Apartment,
  Groups,
  CreditCard,
} from "@mui/icons-material";

import { useEffect, useState } from "react";
import { getLeadById, updateLead, createLead } from "../../api/api";
import { useNavigate, useParams } from "react-router-dom";

import InputField from "../../components/common/InputField";
import SelectField from "../../components/common/SelectField";
import { Checkbox, FormControlLabel } from "@mui/material";
import UploadCard from "../../components/lead/UploadCard";
import MultiUpload from "../../components/lead/MultiUpload";
import { formatCurrency, onlyDigits, limitDigits } from "../../utils/format";
import { showSnackbar } from "../../store/snackbarSlice";
import { useDispatch } from "react-redux";
import { handleLeadRedirect } from "../../utils/leadRedirect";

const iconStyle = { color: "var(--primary)", fontSize: 18 };
const Row = ({ children }: any) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "1fr 1fr",
        md: "1fr 1fr 1fr", // 🔥 normal 2 column
      },
      gap: 2,
      mb: 2,
    }}
  >
    {children}
  </Box>
);

const UploadRow = ({ children }: any) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        sm: "1fr 1fr",
        md: "1fr 1fr 1fr 1.4fr", // 🔥 yaha 4 column
      },
      gap: 2,
      mb: 2,
      alignItems: "stretch",
    }}
  >
    {children}
  </Box>
);

const Section = ({ title }: any) => (
  <>
    <Typography sx={{ fontWeight: 600, mt: 3 }}>{title}</Typography>
    <Divider sx={{ mb: 2, mt: 1 }} />
  </>
);

const CreateLead = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const { id } = useParams();
  const dispatch = useDispatch();

  const location = window.location.pathname;

  const isView = location.includes("/view/");
  const isEdit = location.includes("/edit/");

  const today = new Date();
  const minAgeDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );

  const maxDate = minAgeDate.toISOString().split("T")[0];

  const [form, setForm] = useState<any>({
    customerName: "",
    mobile: "",
    dob: "",
    permanentAddress: "",
    currentAddress: "",
    sameAddress: false,

    productType: "",
    loanAmount: "",
    caseType: "",

    vehicleName: "",
    vehicleNumber: "",
    year: "",
    ownership: "",
    insuranceIdv: "",

    bankName: "",
    branch: "",
    fosName: "",
    fosMobile: "",
    brokerName: "",
    brokerMobile: "",

    coName: "",
    coMobile: "",
    coRelation: "",

    guarantorName: "",
    guarantorMobile: "",

    aadhar: "",
    panNo: "",

    aadharFront: null,
    aadharBack: null,
    pan: null,
    otherDocs: null,
    existingOtherDocs: [],

    existingAadharFront: "",
    existingAadharBack: "",
    existingPan: "",
  });

  useEffect(() => {
    if (id) {
      fetchLead();
    }
  }, [id]);

  useEffect(() => {
    if (form.sameAddress) {
      setForm((prev: any) => ({
        ...prev,
        currentAddress: prev.permanentAddress,
      }));
    }
  }, [form.permanentAddress, form.sameAddress]);

  const fetchLead = async () => {
    try {
      if (!id) return;
      const res = await getLeadById(id);
      const data = res.data?.data;

      if (!isView && data.status !== "login" && data.status !== "pending") {
        handleLeadRedirect(data, navigate);
        return;
      }

      console.log("EDIT DATA:", data);

      setForm({
        // ================= BASIC =================
        customerName: data.customerName || "",
        mobile: data.mobile || "",
        dob: data.dob ? data.dob.slice(0, 10) : "",

        productType: data.productType || "",
        loanAmount: data.loanAmount || "",
        caseType: data.caseType || "",

        // ================= ADDRESS =================
        permanentAddress: data.address?.permanent || "",
        currentAddress: data.address?.current || "",
        sameAddress: false,

        // ================= VEHICLE =================
        vehicleName: data.vehicleDetails?.name || "",
        vehicleNumber: data.vehicleDetails?.number || "",
        year: data.vehicleDetails?.year || "",
        ownership: data.vehicleDetails?.ownership || "",
        insuranceIdv: data.vehicleDetails?.insuranceIdv || "",

        // ================= BANK =================
        bankName: data.bankDetails?.bankName || "",
        branch: data.bankDetails?.branch || "",
        fosName: data.bankDetails?.fosName || "",
        fosMobile: data.bankDetails?.fosMobile || "",
        brokerName: data.bankDetails?.brokerName || "",
        brokerMobile: data.bankDetails?.brokerMobile || "",

        // ================= CO =================
        coName: data.coApplicant?.name || "",
        coMobile: data.coApplicant?.mobile || "",
        coRelation: data.coApplicant?.relation || "",

        // ================= GUARANTOR =================
        guarantorName: data.guarantor?.name || "",
        guarantorMobile: data.guarantor?.mobile || "",

        // ================= DOCUMENT DATA =================
        aadhar: data.documents?.aadharNumber || "",
        panNo: data.documents?.panNumber || "",

        // ================= FILES (NEW UPLOAD) =================
        aadharFront: null,
        aadharBack: null,
        pan: null,
        bank: null,
        itr: null,
        gst: null,
        udyam: null,
        rc: null,
        electricityBill: null,
        otherDocs: null,

        // ================= EXISTING FILES =================
        existingAadharFront: data.documents?.aadharFront || "",
        existingAadharBack: data.documents?.aadharBack || "",
        existingPan: data.documents?.panFile || "",
        existingBank: data.documents?.bankStatement || "",
        existingItr: data.documents?.itr || "",
        existingGst: data.documents?.gst || "",
        existingUdyam: data.documents?.udyam || "",
        existingRc: data.vehicleDetails?.rc || "",
        existingElectricityBill: data.guarantor?.electricityBill || "",
        existingOtherDocs: data.documents?.otherDocs || [],
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));

    // 🔥 remove error on typing
    setErrors((prev: any) => ({
      ...prev,
      [key]: undefined,
    }));
  };

  const validateForm = () => {
    const errors: any = {};

    // ================= REQUIRED =================
    const requiredFields = [
      { key: "customerName", msg: "Enter customer name" },
      { key: "mobile", msg: "Enter mobile number" },
      { key: "productType", msg: "Select product type" },
      { key: "loanAmount", msg: "Enter loan amount" },
      { key: "currentAddress", msg: "Enter current address" },
      { key: "vehicleNumber", msg: "Enter vehicle number" },
      { key: "year", msg: "Enter manufacture year" },
      { key: "aadhar", msg: "Enter Aadhar number" },
      { key: "panNo", msg: "Enter PAN number" },
    ];

    requiredFields.forEach(({ key, msg }) => {
      if (!form[key]?.toString().trim()) {
        errors[key] = msg;
      }
    });

    // ================= FORMAT VALIDATION =================

    // mobile
    if (form.mobile && !/^[0-9]{10}$/.test(form.mobile)) {
      errors.mobile = "Enter valid 10 digit mobile";
    }

    // loan amount
    if (form.loanAmount && isNaN(form.loanAmount)) {
      errors.loanAmount = "Amount must be number";
    }

    // vehicle number
    if (
      form.vehicleNumber &&
      !/^[A-Z]{2}[0-9]{1,2}[A-Z]{1,3}[0-9]{4}$/.test(form.vehicleNumber)
    ) {
      errors.vehicleNumber = "Invalid format (RJ14AB1234)";
    }

    // year
    const currentYear = new Date().getFullYear();

    if (form.year) {
      if (!/^[0-9]{4}$/.test(form.year)) {
        errors.year = "Enter valid 4 digit year";
      } else if (form.year < 1990 || form.year > currentYear) {
        errors.year = `Year must be between 1990 - ${currentYear}`;
      }
    }

    // aadhar
    if (form.aadhar && !/^[0-9]{12}$/.test(form.aadhar)) {
      errors.aadhar = "Aadhar must be 12 digits";
    }

    // pan
    if (form.panNo && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNo)) {
      errors.panNo = "Invalid PAN format";
    }

    // ================= FILE VALIDATION =================

    if (!form.aadharFront && !form.existingAadharFront) {
      errors.aadharFront = "Upload Aadhar front";
    }

    if (!form.pan && !form.existingPan) {
      errors.pan = "Upload PAN card";
    }

    // ================= OPTIONAL =================

    if (form.fosMobile && !/^[0-9]{10}$/.test(form.fosMobile)) {
      errors.fosMobile = "Invalid FOS mobile";
    }

    if (form.coMobile && !/^[0-9]{10}$/.test(form.coMobile)) {
      errors.coMobile = "Invalid co-applicant mobile";
    }

    if (form.guarantorMobile && !/^[0-9]{10}$/.test(form.guarantorMobile)) {
      errors.guarantorMobile = "Invalid guarantor mobile";
    }

    if (form.insuranceIdv && isNaN(form.insuranceIdv)) {
      errors.insuranceIdv = "Invalid IDV amount";
    }

    if (form.dob) {
      const dobDate = new Date(form.dob);
      const today = new Date();

      let age = today.getFullYear() - dobDate.getFullYear();
      const m = today.getMonth() - dobDate.getMonth();

      if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
        age--;
      }

      if (age < 18) {
        errors.dob = "Minimum age must be 18 years";
      }
    }

    return errors;
  };

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);

    try {
      console.log("🚀 Submit Started");

      // 🔥 VALIDATE FIRST
      const validationErrors = validateForm();

      if (Object.keys(validationErrors).length > 0) {
        console.log("❌ Validation Errors:", validationErrors);

        setErrors(validationErrors);

        // 🔥 scroll to first error
        const firstErrorKey = Object.keys(validationErrors)[0];

        document.querySelector(`[name="${firstErrorKey}"]`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        return; // ⛔ stop API call
      }

      // 🔥 CLEAR OLD ERRORS (optional but good)
      setErrors({});

      const formData = new FormData();

      Object.keys(form).forEach((key) => {
        const value = form[key];

        if (value instanceof File) {
          formData.append(key, value);
        } else if (value instanceof FileList) {
          Array.from(value).forEach((file) => {
            formData.append("otherDocs", file);
          });
        } else {
          formData.append(key, value ?? "");
        }
      });

      if (isEdit) {
        // 🔥 UPDATE
        await updateLead(id as string, formData);

        dispatch(
          showSnackbar({
            message: "Lead updated successfully",
            type: "success",
          }),
        );
      } else {
        // 🔥 CREATE
        await createLead(formData);

        dispatch(
          showSnackbar({
            message: "Lead created successfully",
            type: "success",
          }),
        );
      }

      // 🔥 REDIRECT (optional but recommended)
      navigate("/leads");
    } catch (err: any) {
      console.error("❌ API Error:", err);

      if (err?.response) {
        console.error("📛 Server Error:", err.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 3, borderRadius: "14px" }}>
        {/* CUSTOMER */}
        <Section title="Customer Details" />

        <Row>
          <InputField
            name="customerName"
            label="Customer Name"
            required
            value={form.customerName}
            onChange={(e: any) => handleChange("customerName", e.target.value)}
            placeholder="Customer Name"
            icon={<Badge sx={iconStyle} />}
            error={!!errors.customerName}
            helperText={errors.customerName}
            disabled={isView}
          />

          <InputField
            name="mobile"
            label="Customer Mobile"
            type="tel"
            required
            value={form.mobile}
            disabled={isView}
            onChange={(e: any) => {
              let value = e.target.value.replace(/\D/g, "");
              if (value.length > 10) value = value.slice(0, 10);
              handleChange("mobile", value);
            }}
            placeholder="Mobile"
            icon={<Phone sx={iconStyle} />}
            error={!!errors.mobile}
            helperText={
              errors.mobile
                ? errors.mobile
                : `${form.mobile?.length || 0}/10 digits`
            }
          />

          <InputField
            name="dateOfBirth"
            label="Date of Birth"
            type="date"
            value={form.dob || ""}
            onChange={(e: any) => handleChange("dob", e.target.value)}
            disabled={isView}
            inputProps={{
              max: maxDate, // 🔥 18+ only
            }}
            error={!!errors.dob}
            helperText={errors.dob}
          />
        </Row>
        <Row>
          <InputField
            name="permanentAdd"
            label="Permanent Address"
            value={form.permanentAddress}
            disabled={isView}
            onChange={(e: any) =>
              handleChange("permanentAddress", e.target.value)
            }
            placeholder="Permanent Address"
            icon={<LocationOn sx={iconStyle} />}
          />

          <InputField
            name="currentAdd"
            label="Current Address"
            required
            value={form.currentAddress}
            disabled={isView}
            onChange={(e: any) =>
              handleChange("currentAddress", e.target.value)
            }
            placeholder="Current Address"
            icon={<LocationOn sx={iconStyle} />}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.sameAddress || false}
                  disabled={isView}
                  onChange={(e) => {
                    const checked = e.target.checked;

                    handleChange("sameAddress", checked);

                    if (checked) {
                      handleChange("currentAddress", form.permanentAddress);
                    } else {
                      handleChange("currentAddress", "");
                    }
                  }}
                />
              }
              label="Same as Permanent"
            />
          </Box>
        </Row>

        {/* LOAN */}
        <Section title="Loan Details" />

        <Row>
          <SelectField
            name="productType"
            label="Product Type"
            required
            value={form.productType}
            onChange={(e: any) => handleChange("productType", e.target.value)}
            icon={<DirectionsCar sx={iconStyle} />}
            placeholder="Product Type"
            disabled={isView}
            options={[
              { label: "Car", value: "car" },
              { label: "CV", value: "cv" },
              { label: "CE", value: "ce" },
              { label: "Tractor", value: "tractor" },
              { label: "Other", value: "other" },
            ]}
            error={!!errors.productType}
            helperText={errors.productType}
          />

          <InputField
            name="loanAmount"
            label="Loan Amount"
            required
            type="tel" // 🔥 number keyboard ke liye best
            value={form.loanAmount}
            disabled={isView}
            onChange={(e: any) => {
              let value = onlyDigits(e.target.value);
              value = limitDigits(value, 9);
              handleChange("loanAmount", value);
            }}
            placeholder="Enter amount"
            icon={<CurrencyRupee sx={iconStyle} />}
            error={!!errors.loanAmount}
            helperText={
              errors.loanAmount
                ? errors.loanAmount
                : formatCurrency(form.loanAmount)
            }
          />
          <SelectField
            name="caseType"
            label="Case Type"
            value={form.caseType}
            onChange={(e: any) => handleChange("caseType", e.target.value)}
            icon={<CurrencyRupee sx={iconStyle} />}
            placeholder="Case Type"
            disabled={isView}
            options={[
              { label: "New", value: "new" },
              { label: "Purchase", value: "purchase" },
              { label: "Refinance", value: "refinance" },
              { label: "BT + Topup", value: "bt-topup" },
            ]}
          />
        </Row>

        {/* VEHICLE */}
        <Section title="Vehicle Details" />

        <Row>
          <InputField
            name="vehicleName"
            label="Vehicle Name"
            value={form.vehicleName}
            onChange={(e: any) => handleChange("vehicleName", e.target.value)}
            placeholder="Vehicle Name"
            icon={<DirectionsCar sx={iconStyle} />}
            disabled={isView}
          />
          <InputField
            name="vehicleNumber"
            label="Vehicle Number"
            required
            value={form.vehicleNumber}
            disabled={isView}
            onChange={(e: any) => {
              let value = e.target.value.toUpperCase(); // 🔥 uppercase

              // remove invalid chars
              value = value.replace(/[^A-Z0-9]/g, "");

              // limit length (max 10-12)
              if (value.length > 12) value = value.slice(0, 12);

              handleChange("vehicleNumber", value);
            }}
            placeholder="RJ14AB1234"
            icon={<Badge sx={iconStyle} />}
            error={!!errors.vehicleNumber}
            helperText={errors.vehicleNumber}
          />
          <InputField
            name="yearOfManufacture"
            label="Year Of Monufactuere"
            required
            type="tel"
            value={form.year}
            disabled={isView}
            onChange={(e: any) => {
              let value = e.target.value.replace(/\D/g, ""); // only digits

              if (value.length > 4) value = value.slice(0, 4);

              handleChange("year", value);
            }}
            placeholder="e.g. 2020"
            icon={<Description sx={iconStyle} />}
            error={!!errors.year}
            helperText={
              errors.year ? errors.year : `${form.year?.length || 0}/4 digits`
            }
          />
        </Row>

        <Row>
          <InputField
            name="ownership"
            label="Ownership"
            value={form.ownership}
            onChange={(e: any) => handleChange("ownership", e.target.value)}
            placeholder="Ownership"
            disabled={isView}
            icon={<Person sx={iconStyle} />}
          />
          <InputField
            name="insurance"
            label="Insurance IDV"
            value={form.insuranceIdv}
            type="tel"
            disabled={isView}
            onChange={(e: any) => {
              let value = onlyDigits(e.target.value);
              value = limitDigits(value, 9);
              handleChange("insuranceIdv", value);
            }}
            placeholder="Enter IDV amount"
            icon={<CurrencyRupee sx={iconStyle} />}
            helperText={formatCurrency(form.insuranceIdv)} // 🔥 ADD THIS
          />
        </Row>

        {/* BANK */}
        <Section title="Bank Details" />

        <Row>
          <InputField
            name="bankName"
            label="Bank Name"
            value={form.bankName}
            onChange={(e: any) => handleChange("bankName", e.target.value)}
            placeholder="Bank Name"
            disabled={isView}
            icon={<AccountBalance sx={iconStyle} />}
          />
          <InputField
            name="branchName"
            label="Branch Name"
            value={form.branch}
            onChange={(e: any) => handleChange("branch", e.target.value)}
            placeholder="Branch"
            disabled={isView}
            icon={<Apartment sx={iconStyle} />}
          />

          <InputField
            name="fosName"
            label="FOS Name"
            placeholder="FOS Name"
            value={form.fosName}
            onChange={(e: any) => handleChange("fosName", e.target.value)}
            disabled={isView}
            icon={<Person sx={iconStyle} />}
          />
        </Row>

        <Row>
          <InputField
            name="fosMobile"
            label="FOS Mobile"
            placeholder="FOS Mobile"
            value={form.fosMobile}
            type="tel"
            disabled={isView}
            onChange={(e: any) => {
              let value = e.target.value.replace(/\D/g, "");

              // 🔥 limit to 10 digits
              if (value.length > 10) value = value.slice(0, 10);

              handleChange("fosMobile", value);
            }}
            icon={<Phone sx={iconStyle} />}
          />
          <InputField
            name="brokerName"
            label="Broker Name"
            placeholder="Broker Name"
            value={form.brokerName}
            disabled={isView}
            onChange={(e: any) => handleChange("brokerName", e.target.value)}
            icon={<Person sx={iconStyle} />}
          />
          <InputField
            name="brokerMobile"
            label="Broker Mobile"
            placeholder="Broker Mobile"
            value={form.brokerMobile}
            type="tel"
            disabled={isView}
            onChange={(e: any) => {
              let value = e.target.value.replace(/\D/g, "");

              if (value.length > 10) value = value.slice(0, 10);

              handleChange("brokerMobile", value);
            }}
            icon={<Phone sx={iconStyle} />}
          />
        </Row>

        {/* CO-APPLICANT */}
        <Section title="Co-Applicant" />

        <Row>
          <InputField
            name="coApplicatntName"
            label="Co-Applicant Name"
            placeholder="Name"
            value={form.coName}
            disabled={isView}
            onChange={(e: any) => handleChange("coName", e.target.value)}
            icon={<Person sx={iconStyle} />}
          />

          <InputField
            name="coApplicantMobile"
            label="Co-Applicant Mobile"
            placeholder="Mobile"
            value={form.coMobile}
            type="tel"
            disabled={isView}
            onChange={(e: any) => {
              let value = e.target.value.replace(/\D/g, "");

              // 🔥 limit to 10 digits
              if (value.length > 10) value = value.slice(0, 10);

              handleChange("coMobile", value);
            }}
            icon={<Phone sx={iconStyle} />}
          />

          <SelectField
            name="coApplicantRelation"
            label="Co-Applicant Relation"
            value={form.coRelation}
            onChange={(e: any) => handleChange("coRelation", e.target.value)}
            icon={<Groups sx={iconStyle} />}
            placeholder="Relation"
            disabled={isView}
            options={[
              { label: "Father", value: "father" },
              { label: "Mother", value: "mother" },
              { label: "Brother", value: "brother" },
            ]}
          />
        </Row>
        {/* GUARANTOR */}
        <Section title="Guarantor" />

        <Row>
          <InputField
            name="guarantorName"
            label="Guarantor Name"
            placeholder="Name"
            value={form.guarantorName}
            disabled={isView}
            onChange={(e: any) => handleChange("guarantorName", e.target.value)}
            icon={<Person sx={iconStyle} />}
          />

          <InputField
            name="GuarantorMobile"
            label="Guarantor Mobile"
            placeholder="Mobile"
            value={form.guarantorMobile}
            type="tel"
            disabled={isView}
            onChange={(e: any) => {
              let value = e.target.value.replace(/\D/g, "");

              // 🔥 limit to 10 digits
              if (value.length > 10) value = value.slice(0, 10);

              handleChange("guarantorMobile", value);
            }}
            icon={<Phone sx={iconStyle} />}
          />
        </Row>

        {/* DOCUMENT INPUT */}
        <Section title="Documents" />

        <Row>
          <InputField
            name="aadhar"
            label="Customer Aadhar Number"
            required
            type="tel"
            disabled={isView}
            value={form.aadhar}
            onChange={(e: any) => {
              let value = e.target.value.replace(/\D/g, ""); // only digits

              if (value.length > 12) value = value.slice(0, 12);

              handleChange("aadhar", value);
            }}
            placeholder="Enter 12 digit Aadhar"
            icon={<CreditCard sx={iconStyle} />}
            error={!!errors.aadhar}
            helperText={
              errors.aadhar
                ? errors.aadhar
                : `${form.aadhar?.length || 0}/12 digits`
            }
          />
          <InputField
            name="pan"
            label="Customer PAN Number"
            required
            value={form.panNo}
            disabled={isView}
            onChange={(e: any) => {
              let value = e.target.value.toUpperCase(); // 🔥 uppercase

              // allow only valid chars
              value = value.replace(/[^A-Z0-9]/g, "");

              if (value.length > 10) value = value.slice(0, 10);

              handleChange("panNo", value);
            }}
            placeholder="ABCDE1234F"
            icon={<Badge sx={iconStyle} />}
            error={!!errors.panNo}
            helperText={errors.panNo}
          />
        </Row>

        {/* UPLOAD */}
        <UploadRow>
          <UploadCard
            label="Aadhar Front"
            name="aadharFront"
            file={form.aadharFront}
            existingFile={form.existingAadharFront}
            onChange={handleChange}
            error={errors.aadharFront}
            disabled={isView}
          />

          <UploadCard
            label="Aadhar Back"
            name="aadharBack"
            file={form.aadharBack}
            existingFile={form.existingAadharBack}
            onChange={handleChange}
            error={errors.aadharBack}
            disabled={isView}
          />

          <UploadCard
            label="PAN Card"
            name="pan"
            file={form.pan}
            existingFile={form.existingPan}
            onChange={handleChange}
            error={errors.pan}
            disabled={isView}
          />

          <MultiUpload
            files={form.otherDocs}
            existingFiles={form.existingOtherDocs}
            onChange={(files) => handleChange("otherDocs", files)}
            disabled={isView}
          />
        </UploadRow>
        {/* BUTTON */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}
        >
          <Button
            variant="outlined"
            sx={{ borderRadius: "12px" }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>

          {!isView && (
            <Button
              variant="contained"
              disabled={loading}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                px: 3,
                background:
                  "linear-gradient(135deg, var(--primary), var(--primary-light))",
              }}
              onClick={handleSubmit}
            >
              {loading
                ? "Processing..."
                : isEdit
                  ? "Update Lead"
                  : "Create Lead"}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateLead;
